import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Copy,
  Check,
  CreditCard,
  Shield,
  Clock,
  AlertCircle,
  Lock,
  Unlock,
  Package,
  Truck,
} from 'lucide-react';
import MenuClient from '../Components/menuclient';

const API = 'https://appapi.unpaidfinancial.com/api';
const REVEAL_TTL_MS = 60_000;

const fmtMoney = (n, dev = 'EUR') =>
  `${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${dev}`;

const fullName = (u) =>
  (`${u?.prenom ?? ''} ${u?.nom ?? ''}`.trim()) || u?.name || 'Utilisateur';

const groupPAN = (pan) =>
  (pan || '')
    .replace(/\D/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();

export default function CarteVirtuelle() {
  const history = useHistory();

  // ---- session/token ----
  const session = useMemo(() => {
    try {
      const raw = localStorage.getItem('bank.session');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const token = useMemo(() => {
    try {
      return localStorage.getItem('bank.token') || null;
    } catch {
      return null;
    }
  }, []);

  const user = session?.user;
  const account = session?.account;
  const card = session?.card;

  const holder = card?.name_on_card || fullName(user || {});
  const brand = card?.brand || 'VIRTUAL';
  const bin = card?.bin || null;
  const masked = card?.number_masked || '•••• •••• •••• ••••';
  const expM = card?.exp_month || 'MM';
  const expY = card?.exp_year || 'AAAA';
  const devise = account?.devise || 'EUR';
  const actif = !!account?.active;
  const cardId = card?.id;

  // ---- reveal state ----
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState(null);
  const [fullVisible, setFullVisible] = useState(false);
  const [fullPAN, setFullPAN] = useState(null);
  const [fullBIN, setFullBIN] = useState(null);
  const [ttl, setTtl] = useState(Math.floor(REVEAL_TTL_MS / 1000));
  const [copiedField, setCopiedField] = useState(null);

  // ---- physical card state ----
  const [cardEtat, setCardEtat] = useState(card?.request_etat || 'aucune');
  const [physicalModalOpen, setPhysicalModalOpen] = useState(false);
  const [physicalLoading, setPhysicalLoading] = useState(false);
  const [physicalSuccess, setPhysicalSuccess] = useState(false);
  const [physicalError, setPhysicalError] = useState(null);

  const timerRef = useRef(null);
  const abortRef = useRef(null);

  // ---- guards ----
  useEffect(() => {
    if (!session || !session.user || !session.account) {
      history.replace('/banque/login');
    }
  }, [session, history]);

  // ---- fetch card state on mount ----
  useEffect(() => {
    const fetchEtat = async () => {
      if (!token || !cardId) return;
      try {
        const res = await fetch(`${API}/bank/cards/${cardId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await res.json().catch(() => null);
        if (payload?.request_etat) setCardEtat(payload.request_etat);
      } catch (e) {
        console.warn('Erreur récupération état carte', e);
      }
    };
    fetchEtat();
  }, [token, cardId]);

  // ---- cleanup ----
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
      setFullPAN(null);
      setFullBIN(null);
    };
  }, []);

  // ---- reveal handlers ----
  const openConfirm = () => {
    setRevealError(null);
    setConfirmOpen(true);
  };
  const closeConfirm = () => setConfirmOpen(false);

  const doRevealNow = async () => {
    if (!cardId) {
      setRevealError('Carte introuvable dans la session.');
      setConfirmOpen(false);
      return;
    }
    setRevealError(null);
    setRevealLoading(true);

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(`${API}/bank/cards/${cardId}/reveal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Cache-Control': 'no-store',
          Pragma: 'no-cache',
        },
        signal: ctrl.signal,
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok || !payload?.pan) {
        const msg =
          payload?.error ||
          payload?.message ||
          `Impossible d'afficher les détails (HTTP ${res.status}).`;
        throw new Error(msg);
      }

      setFullPAN(payload.pan);
      setFullBIN(payload.bin ?? null);
      setFullVisible(true);
      setTtl(Math.floor(REVEAL_TTL_MS / 1000));
      setConfirmOpen(false);

      if (timerRef.current) clearInterval(timerRef.current);
      const start = Date.now();
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - start;
        const left = Math.max(0, Math.ceil((REVEAL_TTL_MS - elapsed) / 1000));
        setTtl(left);
        if (elapsed >= REVEAL_TTL_MS) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setFullVisible(false);
          setTimeout(() => {
            setFullPAN(null);
            setFullBIN(null);
          }, 50);
        }
      }, 250);
    } catch (e) {
      setRevealError(e?.message || 'Erreur réseau.');
    } finally {
      setRevealLoading(false);
    }
  };

  const copyText = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      alert('Impossible de copier.');
    }
  };

  // ---- physical card handlers ----
  const openPhysicalModal = () => {
    setPhysicalError(null);
    setPhysicalSuccess(false);
    setPhysicalModalOpen(true);
  };
  const closePhysicalModal = () => {
    setPhysicalModalOpen(false);
    setPhysicalSuccess(false);
    setPhysicalError(null);
  };

  const requestPhysicalCard = async () => {
    setPhysicalLoading(true);
    setPhysicalError(null);
    try {
      const res = await fetch(`${API}/bank/cards/request-physical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = payload?.error || payload?.message || `Erreur HTTP ${res.status}`;
        throw new Error(msg);
      }
      setPhysicalSuccess(true);
      setCardEtat('commandee');
      setTimeout(() => {
        closePhysicalModal();
      }, 2500);
    } catch (e) {
      setPhysicalError(e?.message || 'Erreur lors de la demande');
    } finally {
      setPhysicalLoading(false);
    }
  };

  if (!session || !session.user || !session.account) return null;

  const displayBIN = fullVisible ? (fullBIN ?? bin ?? '') : (bin ? '***' : '');

  return (
    <div style={styles.page}>
      {/* HERO */}
      <motion.div style={styles.heroSection} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div style={styles.heroContent}>
          <motion.div
            style={styles.headerTop}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div style={styles.logoContainer}>
              <CreditCard size={28} strokeWidth={2.5} />
              <span style={styles.pageTitle}>Ma Carte Virtuelle</span>
            </div>
            <div
              style={{
                ...styles.statusBadge,
                backgroundColor: actif ? '#16A34A' : '#DC2626',
              }}
            >
              {actif ? 'Actif' : 'Inactif'}
            </div>
          </motion.div>

          <motion.div
            style={styles.balanceCard}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.25, type: 'spring', stiffness: 120 }}
          >
            <div style={styles.balanceLabel}>Solde du compte</div>
            <div style={styles.balanceAmount}>{fmtMoney(account?.solde ?? 0, devise)}</div>
          </motion.div>
        </div>
      </motion.div>

      {/* Notification d'erreur */}
      <AnimatePresence>
        {revealError && (
          <motion.div
            style={styles.errorNotif}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <AlertCircle size={20} color="#DC2626" />
            <span>{revealError}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={styles.contentContainer}>
        {/* CARTE */}
        <motion.div
          style={styles.cardSection}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div style={styles.cardWrapper}>
            <motion.div style={styles.card} whileHover={{ scale: 1.02 }}>
              <div style={styles.cardTop}>
                <div style={styles.brand}>{brand}</div>
                {fullVisible && (
                  <div style={styles.timerBadge}>
                    <Clock size={14} />
                    {ttl}s
                  </div>
                )}
              </div>

              <div style={styles.chip} />

              <motion.div
                style={styles.number}
                key={fullVisible ? 'revealed' : 'masked'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {fullVisible && fullPAN ? groupPAN(fullPAN) : masked}
              </motion.div>

              <div style={styles.bottomRow}>
                <div>
                  <div style={styles.label}>Titulaire</div>
                  <div style={styles.value}>{holder}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={styles.label}>Expire</div>
                  <div style={styles.value}>{`${expM}/${expY}`}</div>
                </div>
              </div>
            </motion.div>

            {/* Bouton révéler */}
            <motion.button
              style={{
                ...styles.revealButton,
                background: fullVisible
                  ? 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)'
                  : 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
              }}
              onClick={fullVisible ? () => {} : openConfirm}
              disabled={!actif || !cardId}
              whileHover={{ scale: actif && cardId ? 1.05 : 1 }}
              whileTap={{ scale: actif && cardId ? 0.95 : 1 }}
            >
              {fullVisible ? (
                <>
                  <Lock size={20} />
                  Masqué automatiquement dans {ttl}s
                </>
              ) : (
                <>
                  <Unlock size={20} />
                  Révéler les détails
                </>
              )}
            </motion.button>

            {/* Bouton/Etat commande carte physique */}
            {cardEtat === 'aucune' || cardEtat === 'refusee' ? (
              <motion.button
                style={styles.physicalButton}
                onClick={openPhysicalModal}
                disabled={!actif}
                whileHover={{ scale: actif ? 1.05 : 1 }}
                whileTap={{ scale: actif ? 0.95 : 1 }}
              >
                <Package size={20} />
                {cardEtat === 'refusee' ? 'Refaire une demande de carte' : 'Commander une carte physique'}
              </motion.button>
            ) : (
              <motion.div style={styles.etatContainer} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {cardEtat === 'commandee' && (
                  <>
                    <Clock size={18} color="#F59E0B" />
                    <span style={styles.etatText}>Demande de carte physique déjà envoyée — en attente de validation</span>
                  </>
                )}
                {cardEtat === 'livraison_en_cours' && (
                  <>
                    <Truck size={18} color="#2563EB" />
                    <span style={styles.etatText}>Carte en cours de livraison</span>
                  </>
                )}
                {cardEtat === 'livree' && (
                  <>
                    <Check size={18} color="#16A34A" />
                    <span style={styles.etatText}>Carte livrée</span>
                  </>
                )}
              </motion.div>
            )}

            {/* Boutons de copie (quand visible) */}
            <AnimatePresence>
              {fullVisible && (
                <motion.div
                  style={styles.copyGrid}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <motion.button
                    style={styles.copyBtn}
                    onClick={() => fullPAN && copyText(fullPAN, 'pan')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {copiedField === 'pan' ? <Check size={16} color="#16A34A" /> : <Copy size={16} />}
                    <span>Numéro</span>
                  </motion.button>

                  <motion.button
                    style={styles.copyBtn}
                    onClick={() => copyText(`${expM}/${expY}`, 'exp')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {copiedField === 'exp' ? <Check size={16} color="#16A34A" /> : <Copy size={16} />}
                    <span>Expiration</span>
                  </motion.button>

                  {(fullBIN ?? bin) && (
                    <motion.button
                      style={styles.copyBtn}
                      onClick={() => copyText(fullBIN ?? bin, 'bin')}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {copiedField === 'bin' ? <Check size={16} color="#16A34A" /> : <Copy size={16} />}
                      <span>BIN</span>
                    </motion.button>
                  )}

                  <motion.button
                    style={styles.copyBtn}
                    onClick={() => copyText(holder, 'holder')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {copiedField === 'holder' ? <Check size={16} color="#16A34A" /> : <Copy size={16} />}
                    <span>Titulaire</span>
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Section infos détaillées */}
        <motion.div
          style={styles.detailsSection}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35 }}
        >
          <h3 style={styles.sectionTitle}>Informations de la carte</h3>

          <div style={styles.detailCard}>
            <div style={styles.detailRow}>
              <div style={styles.detailLeft}>
                <div style={styles.detailLabel}>Statut</div>
                <div
                  style={{
                    ...styles.detailValue,
                    color: actif ? '#16A34A' : '#DC2626',
                    fontWeight: '700',
                  }}
                >
                  {actif ? '✓ Compte actif' : '✗ Compte inactif'}
                </div>
              </div>
            </div>

            {(bin || fullBIN) && (
              <div style={styles.detailRow}>
                <div style={styles.detailLeft}>
                  <div style={styles.detailLabel}>BIN</div>
                  <div style={styles.detailValue}>{displayBIN}</div>
                </div>
                {fullVisible && (
                  <button
                    onClick={() => copyText(fullBIN ?? bin, 'bin-detail')}
                    style={styles.iconButton}
                  >
                    {copiedField === 'bin-detail' ? (
                      <Check size={16} color="#16A34A" />
                    ) : (
                      <Copy size={16} color="#9CA3AF" />
                    )}
                  </button>
                )}
              </div>
            )}

            <div style={styles.detailRow}>
              <div style={styles.detailLeft}>
                <div style={styles.detailLabel}>Numéro de carte</div>
                <div style={{ ...styles.detailValue, fontFamily: 'monospace' }}>
                  {fullVisible && fullPAN ? groupPAN(fullPAN) : masked}
                </div>
              </div>
              {fullVisible && fullPAN && (
                <button
                  onClick={() => copyText(fullPAN, 'pan-detail')}
                  style={styles.iconButton}
                >
                  {copiedField === 'pan-detail' ? (
                    <Check size={16} color="#16A34A" />
                  ) : (
                    <Copy size={16} color="#9CA3AF" />
                  )}
                </button>
              )}
            </div>

            <div style={{ ...styles.detailRow, borderBottom: 'none' }}>
              <div style={styles.detailLeft}>
                <div style={styles.detailLabel}>Date d'expiration</div>
                <div style={styles.detailValue}>{`${expM}/${expY}`}</div>
              </div>
            </div>
          </div>

          {/* Info sécurité */}
          <div style={styles.securityBox}>
            <Shield size={20} color="#0061A6" />
            <div style={styles.securityText}>
              <strong>Sécurité renforcée</strong>
              <p>
                Les détails complets s'affichent pendant {REVEAL_TTL_MS / 1000} secondes puis sont
                automatiquement masqués.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal confirmation reveal */}
      <AnimatePresence>
        {confirmOpen && (
          <motion.div
            style={styles.modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeConfirm}
          >
            <motion.div
              style={styles.modalCard}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalIcon}>
                <Shield size={48} color="#0061A6" />
              </div>
              <h3 style={styles.modalTitle}>Révéler les détails ?</h3>
              <p style={styles.modalText}>
                Vous êtes sur le point d'afficher le <strong>numéro complet de votre carte</strong> en
                clair.
              </p>
              <div style={styles.modalWarning}>
                <AlertCircle size={16} />
                <span>Ne partagez jamais ces informations avec qui que ce soit</span>
              </div>
              <div style={styles.modalButtons}>
                <motion.button
                  style={styles.btnSecondary}
                  onClick={closeConfirm}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Annuler
                </motion.button>
                <motion.button
                  style={styles.btnPrimary}
                  onClick={doRevealNow}
                  disabled={revealLoading || !cardId}
                  whileHover={{ scale: revealLoading || !cardId ? 1 : 1.05 }}
                  whileTap={{ scale: revealLoading || !cardId ? 1 : 0.95 }}
                >
                  {revealLoading ? 'Chargement...' : 'Oui, afficher'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal commande carte physique */}
      <AnimatePresence>
        {physicalModalOpen && (
          <motion.div
            style={styles.modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePhysicalModal}
          >
            <motion.div
              style={styles.modalCard}
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              {physicalSuccess ? (
                <>
                  <div style={styles.modalIcon}>
                    <Check size={48} color="#16A34A" />
                  </div>
                  <h3 style={{ ...styles.modalTitle, color: '#16A34A' }}>Demande envoyée !</h3>
                  <p style={styles.modalText}>
                    Votre demande de carte physique a été enregistrée. Vous recevrez un email dès qu’elle
                    sera en livraison.
                  </p>
                </>
              ) : (
                <>
                  <div style={styles.modalIcon}>
                    <Package size={48} color="#0061A6" />
                  </div>
                  <h3 style={styles.modalTitle}>Commander une carte physique</h3>
                  <p style={styles.modalText}>Une carte sera livrée à l’adresse enregistrée :</p>
                  <div style={styles.addressBox}>
                    <div style={styles.addressValue}>{user?.adresse || 'Adresse non renseignée'}</div>
                  </div>

                  {physicalError && (
                    <div style={{ ...styles.modalWarning, background: '#FEE2E2', color: '#991B1B' }}>
                      <AlertCircle size={16} />
                      <span>{physicalError}</span>
                    </div>
                  )}

                  <div style={styles.modalButtons}>
                    <motion.button
                      style={styles.btnSecondary}
                      onClick={closePhysicalModal}
                      disabled={physicalLoading}
                      whileHover={{ scale: physicalLoading ? 1 : 1.05 }}
                      whileTap={{ scale: physicalLoading ? 1 : 0.95 }}
                    >
                      Annuler
                    </motion.button>
                    <motion.button
                      style={styles.btnPrimary}
                      onClick={requestPhysicalCard}
                      disabled={physicalLoading}
                      whileHover={{ scale: physicalLoading ? 1 : 1.05 }}
                      whileTap={{ scale: physicalLoading ? 1 : 0.95 }}
                    >
                      {physicalLoading ? 'Envoi...' : 'Confirmer la commande'}
                    </motion.button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <MenuClient />
      <div style={{ height: 100 }} />
    </div>
  );
}

// ---- styles ----
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F3F4F6',
    paddingBottom: '80px',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
    color: 'white',
    padding: '24px 20px 32px',
    borderBottomLeftRadius: '24px',
    borderBottomRightRadius: '24px',
    boxShadow: '0 4px 20px rgba(0, 97, 166, 0.3)',
  },
  heroContent: { maxWidth: '480px', margin: '0 auto' },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  logoContainer: { display: 'flex', alignItems: 'center', gap: '10px' },
  pageTitle: { fontSize: '20px', fontWeight: '700', letterSpacing: '-0.5px' },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
  },
  balanceCard: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '20px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    textAlign: 'center',
  },
  balanceLabel: {
    fontSize: '13px',
    opacity: 0.85,
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '500',
  },
  balanceAmount: { fontSize: '36px', fontWeight: '700', letterSpacing: '-1px' },
  errorNotif: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    borderRadius: '12px',
    margin: '20px auto',
    maxWidth: '480px',
    background: '#FEE2E2',
    borderLeft: '4px solid #DC2626',
    color: '#991B1B',
    fontSize: '14px',
    fontWeight: '500',
  },
  contentContainer: { padding: '20px', maxWidth: '480px', margin: '0 auto' },
  cardSection: { marginBottom: '24px' },
  cardWrapper: { display: 'flex', flexDirection: 'column', gap: '16px' },
  card: {
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    color: '#E5E7EB',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
    minHeight: '200px',
    position: 'relative',
    overflow: 'hidden',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  brand: { fontSize: '18px', fontWeight: 800, letterSpacing: '1px', opacity: 0.95 },
  timerBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(239, 68, 68, 0.9)',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '700',
  },
  chip: {
    width: '50px',
    height: '38px',
    background: 'linear-gradient(145deg, #d6bf6a, #b7963f, #a37e2d)',
    borderRadius: '8px',
    marginBottom: '24px',
    opacity: 0.95,
  },
  number: { fontSize: '22px', fontWeight: '700', letterSpacing: '3px', marginBottom: '24px', fontFamily: 'monospace' },
  bottomRow: { display: 'flex', justifyContent: 'space-between' },
  label: { fontSize: '11px', opacity: 0.7, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  value: { fontSize: '14px', fontWeight: '700' },

  revealButton: {
    width: '100%',
    padding: '16px',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.2s ease',
  },
  physicalButton: {
    width: '100%',
    padding: '16px',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
    transition: 'all 0.2s ease',
  },
  etatContainer: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    background: '#F3F4F6',
    color: '#374151',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '8px',
    boxShadow: 'inset 0 0 3px rgba(0,0,0,0.05)',
  },
  etatText: { color: '#374151', fontWeight: 600 },

  copyGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', overflow: 'hidden' },
  copyBtn: {
    background: 'white',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    transition: 'all 0.2s ease',
  },

  detailsSection: { marginTop: '24px' },
  sectionTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '16px' },
  detailCard: { background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '16px' },
  detailRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #F3F4F6' },
  detailLeft: { flex: 1 },
  detailLabel: { fontSize: '12px', color: '#6B7280', marginBottom: '4px', fontWeight: '500' },
  detailValue: { fontSize: '15px', color: '#111827', fontWeight: '600' },
  iconButton: { background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', borderRadius: '8px' },

  securityBox: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    background: 'rgba(0, 97, 166, 0.05)',
    borderRadius: '12px',
    border: '1px solid rgba(0, 97, 166, 0.1)',
  },
  securityText: { flex: 1, fontSize: '13px', color: '#374151', lineHeight: '1.5' },

  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modalCard: {
    background: 'white',
    borderRadius: '24px',
    padding: '32px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
  },
  modalIcon: { display: 'flex', justifyContent: 'center', marginBottom: '20px' },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#111827', textAlign: 'center', marginBottom: '12px', marginTop: 0 },
  modalText: { fontSize: '15px', color: '#6B7280', textAlign: 'center', lineHeight: '1.6', marginBottom: '20px' },
  modalWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    background: '#FEF3C7',
    borderRadius: '12px',
    fontSize: '13px',
    color: '#92400E',
    fontWeight: '500',
    marginBottom: '24px',
  },
  addressBox: { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px', marginBottom: '16px' },
  addressValue: { fontSize: '14px', color: '#111827', lineHeight: '1.6', whiteSpace: 'pre-line' },
  modalButtons: { display: 'flex', gap: '12px' },
  btnSecondary: {
    flex: 1,
    padding: '14px',
    background: 'white',
    color: '#374151',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnPrimary: {
    flex: 1,
    padding: '14px',
    background: 'linear-gradient(135deg, #0061A6 0%, #003D6B 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 97, 166, 0.3)',
  },
};
